import { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import api from "../../../../../constants/api";

export default function ManualTesting({ historyReports, selectedBrowsers }) {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setReports(historyReports);
        runInitialTestCases(historyReports);
    }, [historyReports]);

    const runInitialTestCases = async (initialReports) => {
        const testCasesToRun = initialReports?.filter(item => item.status === "running") || [];
        if (testCasesToRun.length === 0) return;

        setLoading(true);
        for (const testCase of testCasesToRun) {
            try {
                const res = await api.diagnosticHistoryReports.runTestCase(testCase.id);
                if (res?.report) {
                    setReports(prev => {
                        const updated = [...prev];
                        const idx = updated.findIndex(r => r.id === testCase.id);
                        if (idx !== -1) {
                            updated[idx] = {
                                ...updated[idx],
                                status: res.report.status,
                                result: res.report.result,
                            };
                        }
                        return updated;
                    });
                }
            } catch (err) {
                console.error(`Failed to run test ${testCase.id}`, err);
            }
        }
        setLoading(false);
    };


    useEffect(() => {
        const runningReport = reports.some(item => item.status === "running");
        if (runningReport) {
          localStorage.setItem("reports", JSON.stringify(reports));
        } else {
          localStorage.removeItem("reports");
        }
      }, [reports]);
      
    const getStatusColor = (status) => {
        const normalized = status?.toLowerCase?.();
        if (normalized === "success" || normalized === "sucess") return "#4CAF50";
        if (normalized === "failed" || normalized === "faild") return "#F44336";
        return "#757575";
    };

    const getStatusText = (status) => {
        const normalized = status?.toLowerCase?.();
        if (normalized === "success" || normalized === "sucess") return "Success";
        if (normalized === "failed" || normalized === "faild") return "Failed";
        return "Running...";
    };

    const groupedTestCases = Object.values(
        reports?.reduce((acc, item) => {
            const id = item.diagnostic_test_case_id;
            if (!acc[id]) {
                acc[id] = {
                    id,
                    label: item.chrome_report || item.edge_report || item.firefox_report || item.microsoft_report || item.script_report || item.description,
                    browsers: {},
                    status: "",
                };
            }

            const browser = item.browser_name?.toLowerCase();
            if (!browser && item.type === "script") {
                if (item.status) {
                    acc[id].status = item.status;
                }
            }

            if (browser === "chrome") acc[id].browsers.chrome = item.status;
            if (browser === "mozilla") acc[id].browsers.mozilla = item.status;
            if (browser === "edge") acc[id].browsers.edge = item.status;

            return acc;
        }, {})
    );

    return (
        <Paper>
            <Box className="manual_testing">
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        width: "50%",
                                        fontWeight: "medium",
                                        borderBottom: "1px solid #e0e0e0",
                                        borderRight: "1px solid #e0e0e0",
                                    }}
                                >
                                    <p className="manual_test_heading">Select Case Running</p>
                                </TableCell>
                                {selectedBrowsers.map((browser) => (
                                    <TableCell
                                        key={browser.id}
                                        align="center"
                                        sx={{ borderBottom: "1px solid #e0e0e0" }}
                                    >
                                        <Box display="flex" justifyContent="center">
                                            <img src={browser.icon} alt={browser.id} width={24} height={24} />
                                        </Box>
                                    </TableCell>
                                ))}
                                {!selectedBrowsers?.length && (
                                    <TableCell style={{ textAlign: "center" }}>
                                        Status
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {groupedTestCases.map((report) => (
                                <TableRow key={report.id}>
                                    <TableCell
                                        sx={{
                                            py: 1.5,
                                            borderRight: "1px solid #e0e0e0",
                                            borderBottom: "none"
                                        }}
                                    >
                                        {report.label?.trim()}
                                    </TableCell>

                                    {!selectedBrowsers?.length && (
                                        <TableCell
                                            sx={{
                                                py: 1.5,
                                                borderRight: "1px solid #e0e0e0",
                                                borderBottom: "none",
                                                textAlign: "center",
                                                color: getStatusColor(report.status)
                                            }}
                                        >
                                            {getStatusText(report.status)}
                                            {report?.status.toLowerCase() !== "running" && (
                                                <VisibilityIcon fontSize="small" sx={{ color: getStatusColor(report.status) }} />
                                            )}
                                        </TableCell>
                                        
                                    )}

                                    {selectedBrowsers.map((browser) => {
                                        const status = report.browsers?.[browser.id] || "running";
                                        const statusColor = getStatusColor(status);
                                        const statusText = getStatusText(status);

                                        return (
                                            <TableCell
                                                key={browser.id}
                                                align="center"
                                                sx={{ py: 1.5, borderBottom: "none" }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: statusColor,
                                                            fontWeight: status.toLowerCase() === "running" ? "normal" : "medium",
                                                        }}
                                                    >
                                                        {statusText}
                                                    </Typography>
                                                    {status.toLowerCase() !== "running" && (
                                                        <VisibilityIcon fontSize="small" sx={{ color: statusColor }} />
                                                    )}
                                                </Box>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Paper>
    );
}
